import { FileText, ListTodo, Paperclip, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, View, Text, Platform } from "react-native";
import { spacing, colors, radii } from "../../../../styles/design-system";

interface AddToMessageProps {
  onAddAttachment?: () => void;
  onAddTask?: () => void;
  onAddAssignment?: () => void;
}

export const AddToMessage = ({ onAddAttachment, onAddAssignment, onAddTask }: AddToMessageProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuBtnHovered, setIsMenuBtnHovered] = useState(false);

  const [hoveredOption, setHoveredOption] = useState<number | null>(null);
  const menuRef = useRef(null);
  const isWeb = Platform.OS === "web";

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
        setIsMenuBtnHovered(false);
        setHoveredOption(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isWeb]);

  return (
    <View style={styles.container} ref={menuRef}>
      <Pressable
        style={[styles.openMenuBtn, (isMenuBtnHovered || isMenuOpen) && styles.activeMenuBtn]}
        onHoverIn={() => setIsMenuBtnHovered(true)}
        onHoverOut={() => setIsMenuBtnHovered(false)}
        onPress={() => {
          setIsMenuOpen((prev) => !prev);
        }}
        role="button"
        accessibilityLabel="Add to message"
        accessibilityHint="Add an attachment, task, or assignment to the message">
        <Plus size={16} />
      </Pressable>
      {isMenuOpen && (
        <View style={styles.menuList}>
          {onAddAttachment && (
            <Pressable
              role="button"
              style={[styles.menuItem, hoveredOption === 0 && styles.activeMenuItem]}
              onHoverIn={() => setHoveredOption(0)}
              onHoverOut={() => setHoveredOption(null)}
              onPress={() => {
                onAddAttachment();
                setIsMenuOpen(false);
              }}
              accessibilityLabel="Add attachment"
              accessibilityHint="Add an attachment to the message">
              <Paperclip size={16} className="mr-2" />
              <Text numberOfLines={1}>Add attachment</Text>
            </Pressable>
          )}
          {onAddTask && (
            <Pressable
              role="button"
              style={[styles.menuItem, hoveredOption === 1 && styles.activeMenuItem]}
              onHoverIn={() => setHoveredOption(1)}
              onHoverOut={() => setHoveredOption(null)}
              onPress={() => {
                onAddTask();
                setIsMenuOpen(false);
              }}
              accessibilityLabel="Add task"
              accessibilityHint="Add a task to the message">
              <ListTodo size={16} className="mr-2" />
              <Text numberOfLines={1}>Add task</Text>
            </Pressable>
          )}
          {onAddAssignment && (
            <Pressable
              role="button"
              style={[styles.menuItem, hoveredOption === 2 && styles.activeMenuItem]}
              onHoverIn={() => setHoveredOption(2)}
              onHoverOut={() => setHoveredOption(null)}
              onPress={() => {
                onAddAssignment();
                setIsMenuOpen(false);
              }}
              accessibilityLabel="Add assignment"
              accessibilityHint="Add an assignment to the message">
              <FileText size={16} className="mr-2" />
              <Text numberOfLines={1}>Add assignment</Text>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
    width: spacing.xl,
    height: spacing.xl,
  },
  openMenuBtn: {
    padding: radii.sm,
    borderRadius: radii.full,
    aspectRatio: 1,
    color: colors.grey[500],
    backgroundColor: colors.white,
  },
  activeMenuBtn: {
    backgroundColor: colors.grey[50],
    color: colors.grey[900],
  },
  menuList: {
    position: "absolute",
    marginBottom: spacing.xs,
    bottom: "100%",
    left: 0,
    backgroundColor: colors.white,
    borderRadius: radii.md,
    padding: spacing.xxs,
    borderWidth: 1,
    borderColor: colors.grey[200],
    shadowColor: colors.grey[900],
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  menuItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: spacing.xs,
    paddingRight: spacing.xl,
    gap: spacing.xs,
    borderRadius: radii.sm,
    color: colors.grey[900],
  },
  activeMenuItem: {
    backgroundColor: colors.grey[100],
  },
});
